<template>
  <div class="top-customers-chart">
    <div v-if="loading" class="chart-loading">
      <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
      <span>数据加载中...</span>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-user"></i>
      <span>暂无数据</span>
    </div>
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'top-customers-chart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.length > 0
    },

    chartData() {
      if (!this.hasData) return []
      return this.data.slice(0, 10).reverse()
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.renderChart()
    },

    renderChart() {
      if (!this.hasData) {
        if (this.chart) {
          this.chart.dispose()
          this.chart = null
        }
        return
      }

      if (!this.chart) return

      // 计算最大金额，决定使用什么单位
      const maxAmount = Math.max(...this.chartData.map(item => Number(item.totalAmount)))
      const useWanUnit = maxAmount >= 10000 // 大于等于1万元使用万元单位

      // 格式化金额的辅助函数
      const formatAmount = (value) => {
        if (useWanUnit) {
          const wan = value / 10000
          if (wan >= 100) {
            return '¥' + wan.toFixed(1) + 'w'
          } else if (wan >= 10) {
            return '¥' + wan.toFixed(2) + 'w'
          } else {
            return '¥' + wan.toFixed(3) + 'w'
          }
        } else {
          // 小于1万元，直接显示元
          return '¥' + value.toFixed(2)
        }
      }

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            const item = params[0]
            const dataIndex = item.dataIndex
            const customerData = this.chartData[dataIndex]
            const fullName = customerData.customerName || customerData.customerCode
            const amount = Number(item.value)
            return `${fullName}<br/>支付金额: ¥${amount.toFixed(2)}`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: value => {
              if (useWanUnit) {
                const wan = value / 10000
                if (wan >= 100) {
                  return '¥' + wan.toFixed(0) + 'w'
                } else if (wan >= 10) {
                  return '¥' + wan.toFixed(1) + 'w'
                } else if (wan >= 1) {
                  return '¥' + wan.toFixed(2) + 'w'
                } else {
                  return '¥' + wan.toFixed(3) + 'w'
                }
              } else {
                // 小于1万，显示整数元
                if (value >= 1000) {
                  return '¥' + (value / 1000).toFixed(1) + 'k'
                } else {
                  return '¥' + value.toFixed(0)
                }
              }
            }
          },
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#e0e0e0'
            }
          }
        },
        yAxis: {
          type: 'category',
          data: this.chartData.map(item => {
            const name = item.customerName || item.customerCode
            // 客户名称超过12个字符时截断
            return name.length > 12 ? name.substring(0, 12) + '...' : name
          }),
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            interval: 0,
            formatter: (value) => value
          }
        },
        series: [{
          type: 'bar',
          data: this.chartData.map(item => Number(item.totalAmount)),
          barWidth: '60%',
          itemStyle: {
            borderRadius: [0, 8, 8, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#667eea' },
              { offset: 1, color: '#764ba2' }
            ])
          },
          label: {
            show: true,
            position: 'right',
            formatter: params => formatAmount(params.value),
            fontSize: 11,
            color: '#666'
          }
        }]
      }

      this.chart.setOption(option, true)
    },

    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    }
  }
}
</script>

<style scoped>
.top-customers-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 350px;
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 320px;
  color: #909399;
  font-size: 14px;
}

.chart-loading i,
.chart-empty i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}
</style>




// WEBPACK FOOTER //
// src/custom/components/stats/pay-stats/components/top-customers-chart.vue